import { RepositoryInterface } from '@cundd/rest-adapter';
import * as React from 'react';
import { BrowserRouter as Router, Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import './App.css';
import { AppContext } from './AppContextType';
import { AppMode } from './AppMode';
import { FilterView as CandidateFilterView } from './Components/Candidate/FilterView';
import { CandidateDetail } from './Components/CandidateDetail';
import { CandidateList } from './Components/CandidateList';
import { DepartmentDescription } from './Components/DepartmentDescription';
import { DetailProps } from './Components/DetailProps';
import { FilterProps } from './Components/FilterProps';
import { FilterView as JobVacancyFilterView } from './Components/JobVacancy/FilterView';
import { JobVacancyDetail } from './Components/JobVacancyDetail';
import { JobVacancyList } from './Components/JobVacancyList';
import { ListProps } from './Components/ListProps';
import { Loading } from './Components/Loading';
import ScrollTo from './Components/ScrollTo';
import { Config } from './Config';
import { Filter } from './Filter/Filter';
import { FilterSettings } from './Filter/FilterSettings';
import { HandlerContextProvider } from './HandlerContextProvider';
import { AppState, MainHandler } from './MainHandler';
import { Candidate } from './Model/Candidate';
import { JobCenterModelInterface } from './Model/JobCenterModelInterface';
import { JobVacancy } from './Model/JobVacancy';
import { ReferenceIdContainer, ReferenceIdContainerInterface } from './ReferenceIdContainer';
import { RouteConstants } from './RouteConstants';
import { ServiceLocator } from './ServiceLocator';
import { StaticSettings } from './StaticSettings';
import { Type } from './Type';

export interface AppProps<T extends Candidate | JobVacancy | JobCenterModelInterface> {
    serviceLocator: ServiceLocator;
    handler: MainHandler<T>;
}

interface AppStateLocal<T extends JobCenterModelInterface> extends AppState<T> {
    error?: Error | string;
}

interface ListRouteParams {
    pageNumber?: string;
}

interface DetailRouteParams {
    referenceId: string;
}

function cleanUpSelectedJobVacancies(
    selectedJobVacancies: ReferenceIdContainerInterface,
    jobVacancies: JobVacancy[]
): ReferenceIdContainerInterface {
    const currentJobVacancies = jobVacancies.map(j => j.referenceId);

    return new ReferenceIdContainer(selectedJobVacancies.identifiers.filter(i => currentJobVacancies.indexOf(i) > -1));
}

export class App<T extends Candidate | JobVacancy | JobCenterModelInterface> extends React.Component<AppProps<T>, AppStateLocal<T>> {
    private readonly config: Config;
    private readonly filter: Filter;

    constructor(props: AppProps<T>, context: AppContext<T>) {
        super(props, context);

        this.handleChange = this.handleChange.bind(this);

        const serviceLocator = props.serviceLocator;
        this.config = serviceLocator.get('config');
        this.state = this.props.handler.state;
        this.filter = new Filter();
    }

    public render() {
        const props = this.props;

        const renderInner = () => {
            const state = this.state;
            if (state.jobVacancies === undefined || state.candidates === undefined) {
                return <div id="job-center" className="job-center job-center-app">
                    <Loading/>
                </div>;
            }

            return <div id="job-center" className="job-center job-center-app">
                <Router basename={this.config.basePath}>
                    <ScrollTo/>
                    {this.renderForMode()}
                </Router>
            </div>;
        };

        return <HandlerContextProvider handler={props.handler} serviceLocator={props.serviceLocator}>
            {renderInner()}
        </HandlerContextProvider>;
    }

    private renderForMode() {
        if (StaticSettings.mode === AppMode.Both) {
            return <Switch>
                {/* Routing for Job Vacancy listing */}
                <Route path={RouteConstants.JobVacancy}
                       exact={true}
                       render={(routeProps) => this.renderList(
                           Type.JobVacancy,
                           routeProps
                       )}/>
                {/* Routing for Job Vacancy listing with pagination */}
                <Route path={RouteConstants.JobVacancy + '/page/:pageNumber'}
                       exact={true}
                       render={(routeProps) => this.renderList(
                           Type.JobVacancy,
                           routeProps
                       )}/>
                {/* Routing for Candidate listing */}
                <Route path={RouteConstants.Candidate}
                       exact={true}
                       render={(routeProps) => this.renderList(
                           Type.Candidate,
                           routeProps
                       )}/>

                {/* Routing for Job Vacancy detail */}
                <Route path={RouteConstants.JobVacancy + '/:referenceId'}
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.JobVacancy,
                           routeProps
                       )}/>
                <Route path="/stellenangebot/:referenceId"
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.JobVacancy,
                           routeProps
                       )}/>

                {/* Routing for Candidate detail */}
                <Route path={RouteConstants.Candidate + '/:referenceId'}
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.Candidate,
                           routeProps
                       )}
                />
                <Route path="/kandidat/:referenceId"
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.Candidate,
                           routeProps
                       )}
                />

                <Route render={() => <Redirect to={RouteConstants.JobVacancy}/>}/>
            </Switch>;
        } else if (StaticSettings.mode === AppMode.JobVacancy) {
            return <Switch>
                {/* Routing for Job Vacancy listing */}
                {<Route path="/"
                        exact={true}
                        render={(routeProps) => this.renderList(
                            Type.JobVacancy,
                            routeProps
                        )}/>}

                {/* Routing for Job Vacancy listing with pagination */}
                {<Route path="/page/:pageNumber"
                        exact={true}
                        render={(routeProps) => this.renderList(
                            Type.JobVacancy,
                            routeProps
                        )}/>}

                {/* Routing for Job Vacancy detail */}
                <Route path="/:referenceId"
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.JobVacancy,
                           routeProps
                       )}/>

                <Route render={() => <Redirect to="/"/>}/>
            </Switch>;
        } else if (StaticSettings.mode === AppMode.Candidate) {
            return <Switch>
                {/* Routing for Candidate listing */}
                <Route path={RouteConstants.Candidate}
                       exact={true}
                       render={(routeProps) => this.renderList(
                           Type.Candidate,
                           routeProps
                       )}/>

                {/* Routing for Candidate detail */}
                <Route path="/:referenceId"
                       exact={true}
                       render={(routeProps) => this.renderDetail(
                           Type.Candidate,
                           routeProps
                       )}
                />

                <Route render={() => <Redirect to="/"/>}/>
            </Switch>;
        }
    }

    private renderList(type: Type, routeProps: RouteComponentProps<ListRouteParams>): JSX.Element {
        const requestedPageNumber = this.getRequestedPageNumber(routeProps);
        const allModels = this.getAllModelsForType(type);
        const filteredModels = this.filteredModels(allModels);
        const state = this.state;
        const props: ListProps<any> = {
            allModels,
            filteredModels,
            filterSettings: state.filterSettings,
            selectedJobVacancies: state.selectedJobVacancies,
            onChange: this.handleChange,
            requestedPageNumber,
            history: routeProps.history
        };

        const config = this.config;
        const departmentDescription = config.department
            ? <DepartmentDescription department={config.department}/>
            : null;

        switch (type) {
            case Type.Candidate:
                return <div className="job-center-list job-center-candidate-list">
                    {this.renderFilter(type, allModels, filteredModels, routeProps)}
                    {departmentDescription}
                    <CandidateList {...routeProps} {...props}/>
                </div>;
            case Type.JobVacancy:
                return <div className="job-center-list job-center-job-vacancy-list">
                    {this.renderFilter(type, allModels, filteredModels, routeProps)}
                    {departmentDescription}
                    <JobVacancyList {...routeProps} {...props}/>
                </div>;
        }
    }

    private renderDetail(type: Type, routeProps: RouteComponentProps<DetailRouteParams>) {
        const allModels = this.getAllModelsForType(type);
        const filteredModels = this.filteredModels(allModels);
        const referenceId = routeProps.match.params.referenceId;
        console.debug(`[App] Show detail of REF${referenceId}`);

        const model = allModels.find((m) => m.referenceId === referenceId);
        if (model === undefined) {
            const label = type === Type.Candidate ? 'Der Kandidat' : 'Das Stellenangebot';

            return <div className="alert alert-danger">{label} mit der Ref {referenceId} wurde nicht gefunden</div>;
        }
        const props: DetailProps<any> = {
            model: model,
            allModels,
            filteredModels: filteredModels,
            filterSettings: this.state.filterSettings,
            selectedJobVacancies: this.state.selectedJobVacancies,
            onChange: this.handleChange,
            history: routeProps.history
        };

        const filter = StaticSettings.ui.detail.showFilter
            ? this.renderFilter(type, allModels, filteredModels, routeProps)
            : null;

        switch (type) {
            case Type.Candidate:
                return <div className="job-center-detail job-center-candidate-detail">
                    {filter}
                    <CandidateDetail {...routeProps} {...props}/>
                </div>;
            case Type.JobVacancy:
                return <div className="job-center-detail job-center-job-vacancy-detail">
                    {filter}
                    <JobVacancyDetail {...routeProps} {...props}/>
                </div>;
        }
    }

    private renderFilter(
        type: Type,
        allModels: JobCenterModelInterface[],
        filteredModels: JobCenterModelInterface[],
        routeProps: RouteComponentProps<ListRouteParams | DetailRouteParams>
    ) {
        const state = this.state;
        const props: FilterProps<any> = {
            allModels,
            filteredModels,
            type,
            filterSettings: state.filterSettings,
            selectedJobVacancies: state.selectedJobVacancies,
            onChange: this.handleChange,
            history: routeProps.history
        };

        switch (type) {
            case Type.Candidate:
                return <CandidateFilterView {...props}/>;
            case Type.JobVacancy:
                return <JobVacancyFilterView {...props}/>;
        }
    }

    private filteredModels<T extends JobCenterModelInterface>(models: T[]): T[] {
        return this.filter.filter(models, this.state.filterSettings);
    }

    private getRequestedPageNumber(routeProps: RouteComponentProps<ListRouteParams>): number | undefined {

        const pageNumberRaw = routeProps.match.params.pageNumber;
        if (!pageNumberRaw) {
            return undefined;
        }

        const pageNumber = parseInt(pageNumberRaw, 10);

        return !isNaN(pageNumber) ? pageNumber : undefined;
    }

    public componentDidMount(): void {
        const sl = this.props.serviceLocator;
        const candidateRepository: RepositoryInterface<Candidate> = sl.get('candidateRepository');
        const jobVacancyRepository: RepositoryInterface<JobVacancy> = sl.get('jobVacancyRepository');

        Promise.all([candidateRepository.findAll(), jobVacancyRepository.findAll()])
            .then(results => {
                const candidatesResult = results[0];
                const jobVacanciesResult = results[1];

                this.setState(
                    ps => {
                        const candidates = Array.from(candidatesResult.values()) as any;
                        const jobVacancies = Array.from(jobVacanciesResult.values()) as any;
                        return {
                            candidates,
                            jobVacancies,
                            selectedJobVacancies: cleanUpSelectedJobVacancies(ps.selectedJobVacancies, jobVacancies)
                        };
                    }
                );
            })
            .catch(e => console.error(e));
    }

    public handleChange(filterSettings: FilterSettings): void {
        this.setState({filterSettings});
    }

    private getAllModelsForType(type: Type): JobCenterModelInterface[] {
        switch (type) {
            case Type.Candidate:
                return this.state.candidates || [];
            case Type.JobVacancy:
                return this.state.jobVacancies || [];
        }
    }
}
